import { compileDefinitionInputs, definitionReaders, type DefinitionRow } from '../janus-engine/authoring/location-type-ambience';
import { conditionalShapeshift, formCardReferenceIds, formCardUuid, preparedSpellGroups, savedShapeshiftChoice,
    shapeshiftChoices, shapeshiftChoiceSpec, shapeshiftNeedsCandidates, shapeshiftOptions, type FormCardReaders, type FormOption } from '../janus-engine/authoring/form-card-extras';
import { layerEntriesOf, scalarLayerSlot } from './helpers';
import { renderSingleItem, renderUserChoiceListRead, renderCardListItemLayerRead, type RenderBlob, type RenderCtx } from './primitives';

const read: FormCardReaders = { ...definitionReaders, entries: layerEntriesOf };
type Context = { source_id: string; form?: boolean; pc_id?: string; selected?: unknown; caster?: boolean; actor_id?: unknown };
type Inputs = { source_id: string; creature_ids: string[]; choices: DefinitionRow[] };
type FormHost = Window & { initializeTomSelect?: () => void; removeSelectUserChoices?: (layer: string) => void };
const host = window as FormHost;
const contexts = new WeakMap<HTMLElement, { key: string; section: string | null; pending: boolean }>();

async function fetchInputs(context: Context, creatures = false): Promise<Inputs> {
    const response = await fetch(BASE_URL + 'options/api/creator_form_card_inputs_inc.php', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_id: context.source_id, pc_id: context.pc_id ?? '', creatures }),
    });
    const body = await response.json() as Inputs & { error?: string };
    if (!response.ok || body.error || body.source_id !== context.source_id.toUpperCase())
        throw new Error(body.error || 'Form card inputs unavailable. Please retry.');
    return body;
}

function selectHtml(layer: string, source: string, options: readonly FormOption[], selected: string, retainedName = selected): string {
    const conditional = layer === 'shapeshift_creature';
    const id = conditional ? layer : `${layer}-${source}_`;
    const name = conditional ? layer : `${layer}[${source}_]`;
    const retained = selected && !options.some(option => option.id === selected)
        ? [...options, { id: selected, name: retainedName || selected }] : options;
    return `<select class="${conditional ? 'ts-select' : 'form-select mb-2 ts-select select_user_choice shapeshift'}"`
        + ` id="${escapeHtml(id)}" name="${escapeHtml(name)}" title="Shapeshift Creature" data-ft-open-item-info-value="1"`
        + (conditional ? '' : ' data-show-icons="false" data-user-choice-layer="shapeshift"')
        + ` data-user-choice-selected="${escapeHtml(selected)}"><option value="">--SELECT--</option>`
        + retained.map(option => `<option value="${escapeHtml(option.id)}" data-tokens="${escapeHtml(option.name)}"${option.id === selected ? ' selected' : ''}>${escapeHtml(option.name)}</option>`).join('') + '</select>';
}

const block = (header: string, rows: string, color = 'primary'): string => '<div class="card-text ft-ld-block"><ul class="list-group border">'
    + `<li class="list-group-item list-group-item-${color} fw-bold" aria-current="true">${header}</li>${rows}</ul></div>`;

export function renderFormShapeshifts(context: Context, source: DefinitionRow, definitions: ReadonlyMap<string, DefinitionRow | null>,
    candidates: readonly DefinitionRow[], choices: readonly DefinitionRow[], selections: ReadonlyMap<string, string> = new Map()): string {
    const spec = conditionalShapeshift(source, read);
    const names = Object.fromEntries([...definitions].flatMap(([id, row]) => row
        ? [[id, { item_name: String(read.item(row, 'item_name') ?? ''), item_type: String(read.item(row, 'item_type') ?? '') }]] : []));
    const ctx: RenderCtx = { arr: source as RenderBlob, names };
    const selected = selections.get('shapeshift_creature') ?? formCardUuid(context.selected);
    let html = '';
    if (spec) html = block(iconMap('shapeshift') + ' Shapeshift:', '<li class="list-group-item">'
        + selectHtml('shapeshift_creature', context.source_id, shapeshiftOptions(spec, candidates, read),
            selected, names[selected]?.item_name) + '</li>');
    else if (!context.form) html = renderUserChoiceListRead(ctx, 'shapeshift', { label: 'Shapeshift', headerClass: 'list-group-item-primary border fw-bold' });
    if (context.form || spec) {
        const option = (entry: unknown): FormOption | null => {
            const id = formCardUuid(entry), row = definitions.get(id);
            return row ? { id, name: String(read.item(row, 'item_name') ?? '') } : null;
        };
        let rows = '';
        if (!spec && context.form) rows += renderCardListItemLayerRead(ctx, 'shapeshift', read.entries(source, 'shapeshift'));
        for (const layer of shapeshiftChoices) {
            const choiceSpec = shapeshiftChoiceSpec(source, layer, read);
            const options = choiceSpec ? shapeshiftOptions(choiceSpec, candidates, read)
                : read.entries(source, layer).map(option).filter((value): value is FormOption => value !== null);
            if (!options.length) continue;
            const name = `${layer}[${context.source_id}_]`;
            const saved = selections.get(name) ?? savedShapeshiftChoice(choices, layer, context.source_id);
            rows += `<li class="list-group-item">${context.form
                ? selectHtml(layer, context.source_id, options, saved, names[saved]?.item_name)
                : options.map(value => `<span role="button" data-ft-open-item-info="${escapeHtml(value.id)}">${escapeHtml(value.name)}</span>`).join(', ')}</li>`;
        }
        if (rows) html += block(iconMap('shapeshift') + ' Shapeshift:', rows);
    }
    if (!html) return '';
    html += renderSingleItem(ctx, 'shapeshift_inventory_remains_on_form', { contentType: 'label_only', label: 'Shapeshift Inventory Remains on Form:', liClass: 'list-group-item-success fw-bold', extraHtmlCallback: () => '<li class="list-group-item">When shapeshifting, the caster keeps all the inventory equipped into the shapeshifted form.</li>' });
    html += renderSingleItem(ctx, 'user_choice_1_shapeshift_inventory_remains_on_form', { contentType: 'label_only', label: 'User Choice Shapeshift Inventory Remains on Form:', liClass: 'list-group-item-success fw-bold', extraHtmlCallback: () => '<li class="list-group-item">When shapeshifting, the caster has a choice to keep all the inventory the caster has on the new form, or absorb it (default logic) into the shapeshifted form.</li>' });
    return html;
}

export function renderFormPrepared(context: Context, source: DefinitionRow, definitions: ReadonlyMap<string, DefinitionRow | null>): string {
    const groups = preparedSpellGroups(source, definitions, read);
    if (!groups.length) return '';
    const essence = scalarLayerSlot(read.layer(source, 'spellcasting_essence'));
    const casting = 'F964C0DA-54EA-1EB5-ED00-8E9AD50FFA53';
    return block((essence ? iconMap(essence) + ' ' : '') + 'Spellcasting'
        + (context.caster ? ` | <button class="btn btn-primary border text-dark" type="button" data-ft-use-ability="${casting}">${iconMap(casting)}</button>` : ''),
    groups.map(group => `<li class="list-group-item list-group-item-secondary fw-bold">${iconMap('spell_prepared_1')} Spells Prepared${group.label}:</li>`
        + group.levels.flatMap((options, level) => options.length ? [`<li class="list-group-item">${iconMap('spell_level_' + level)} | `
            + options.map(option => '<span class="badge bg-light text-dark">'
                + (context.actor_id ? `<span role="button" data-ft-use-ability="${option.id}" class="mr-1">${iconMap('cast_spell')}</span>` : '')
                + `<a role="button" data-ft-open-item-info="${option.id}" href="${escapeHtml(BASE_URL + 'item.php?id=' + option.id)}">${escapeHtml(option.name)}</a></span>`).join(' ') + '</li>'] : []).join('')).join(''), 'info');
}

export async function mountFormCardExtras(el: HTMLElement): Promise<void> {
    const script = el.querySelector('.ft-form-card-context');
    const previous = contexts.get(el);
    const key = script?.textContent ?? previous?.key;
    if (!key) return;
    const section = el.getAttribute('data-ft-form-card-extras');
    const context = JSON.parse(key) as Context;
    const state = { key, section, pending: true };
    contexts.set(el, state);
    // The existing creator validator also protects autosave. An unnamed, disabled select
    // pauses that validator until these async controls exist; it can never enter a save body.
    if (context.form && !el.querySelector('[data-ft-form-card-pending]')) {
        const pending = document.createElement('select');
        pending.className = 'select_user_choice'; pending.disabled = true; pending.hidden = true;
        pending.setAttribute('data-ft-form-card-pending', '1');
        pending.setAttribute('data-user-choice-layer', 'form options loading'); el.append(pending);
    }
    const current = (): boolean => el.isConnected && contexts.get(el) === state
        && el.getAttribute('data-ft-form-card-extras') === section
        && (!el.querySelector('.ft-form-card-context') || el.querySelector('.ft-form-card-context')?.textContent === key);
    try {
        let inputs = await fetchInputs(context);
        if (!current()) return;
        let definitions = await compileDefinitionInputs(inputs, [context.source_id]);
        let source = definitions.get(context.source_id.toUpperCase());
        if (!current()) return;
        if (!source) throw new Error('Form card definition unavailable. Please retry.');
        if (section === 'shapeshifts' && shapeshiftNeedsCandidates(source, read)) {
            inputs = await fetchInputs(context, true);
            if (!current()) return;
            definitions = await compileDefinitionInputs(inputs, [context.source_id]);
            source = definitions.get(context.source_id.toUpperCase());
            if (!current()) return;
            if (!source) throw new Error('Form card definition unavailable. Please retry.');
        }
        const savedIds = inputs.choices.filter(row => formCardUuid(read.item(row, 'choice_item_id')) === context.source_id.toUpperCase())
            .map(row => formCardUuid(read.item(row, 'choice'))).filter(Boolean);
        const references = await compileDefinitionInputs(inputs, [...formCardReferenceIds(source, read), ...savedIds,
            ...(section === 'shapeshifts' ? inputs.creature_ids : [])]);
        if (!current()) return;
        for (const [id, row] of references) definitions.set(id, row);
        // Read at paint time: a user may change a control while the request is pending.
        const selections = new Map(previous?.key === key && previous.section === section
            ? Array.from(el.querySelectorAll<HTMLSelectElement>('select[name]'), select => [select.name, select.value]) : []);
        const html = section === 'shapeshifts'
            ? renderFormShapeshifts(context, source, definitions, inputs.creature_ids.flatMap(id => {
                const row = definitions.get(id.toUpperCase()); return row ? [row] : [];
            }), inputs.choices, selections)
            : renderFormPrepared(context, source, definitions);
        el.querySelectorAll<HTMLSelectElement>('select').forEach(select => (select as HTMLSelectElement & { tomselect?: { destroy(): void } }).tomselect?.destroy());
        el.innerHTML = html;
        el.setAttribute('data-ft-form-card-done', '1');
        host.initializeTomSelect?.();
    } catch (error) {
        if (!current()) return;
        el.removeAttribute('data-ft-form-card-done');
        // Keep any existing selections available while a failed raw read is retried.
        let message = el.querySelector<HTMLElement>('[data-ft-form-card-error]');
        if (!message) { message = document.createElement('div'); message.setAttribute('data-ft-form-card-error', '1'); el.append(message); }
        message.textContent = error instanceof Error ? error.message : 'Form card unavailable. Please retry.';
        message.className = 'text-danger';
    } finally {
        state.pending = false;
    }
}

export function mountCreatorFormExtras(root: ParentNode = document): void {
    if (!window.ftAdoptInventoryBase) return; // The DOM-ready scan resumes after the page owner registers.
    const mounts = Array.from(root.querySelectorAll<HTMLElement>('[data-ft-form-card-extras]'));
    if (root instanceof HTMLElement && root.matches('[data-ft-form-card-extras]')) mounts.unshift(root);
    mounts.forEach(el => {
        const state = contexts.get(el), key = el.querySelector('.ft-form-card-context')?.textContent;
        if (!state || (key && key !== state.key) || el.getAttribute('data-ft-form-card-extras') !== state.section
            || (!state.pending && !el.hasAttribute('data-ft-form-card-done')))
            void mountFormCardExtras(el);
    });
}

// Creator AJAX panels call loadPostFunctions without the list-display scan. Inspect only newly
// inserted subtrees, never re-render or scan all cards in response to unrelated page mutations.
function observeFormCards(): void {
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) return;
        const mount = node.closest<HTMLElement>('[data-ft-form-card-extras]');
        mountCreatorFormExtras(mount ?? node);
    }))).observe(document.body, { childList: true, subtree: true });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeFormCards, { once: true });
else observeFormCards();

document.addEventListener('click', event => {
    const select = event.target instanceof Element ? event.target.closest('select.select_user_choice.shapeshift') : null;
    if (select?.closest('[data-ft-form-card-extras]')) host.removeSelectUserChoices?.('shapeshift');
});
